import * as R from 'ramda';
import * as P from 'plow-js';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature dispatch-planner
import * as C from './constants';
import {
  sortTelEvents,
  concatArrayItemsProp,
  filterPickedDroppedItems,
  getAvailableContainersForDrop,
  indexObjectArrayPropByPropName,
} from './shared/helpers';
//////////////////////////////////////////////////

// COMMON
export const getLoadName = (load, newNameText = '') => R.pathOr(
  newNameText,
  [GC.FIELD_PRIMARY_REFERENCE, GC.FIELD_VALUE],
  load,
);

export const getCloItemsInTelByEventType = (tel, eventType) => R.compose(
  R.filter(G.isLoadTypeClo),
  (events) => concatArrayItemsProp(events, GC.FIELD_LOAD_ITEMS),
  R.filter((event) => R.propEq(eventType, GC.FIELD_EVENT_TYPE, event)),
  R.values,
)(tel[GC.FIELD_LOAD_STOPS]);

const getTelCloItemsByType = (tel, type) => R.compose(
  R.filter(G.isLoadTypeClo),
  R.reduce((acc, stop) => R.concat(acc, stop.items), []),
  R.filter(R.propEq(type, GC.FIELD_EVENT_TYPE)),
  R.values,
  R.prop(GC.FIELD_LOAD_STOPS),
)(tel);

const getTerminalCloItemsByType = (tels, telGuid, stopId, type) => {
  let telsToUse = tels;

  if (R.equals(type, GC.EVENT_TYPE_DROP)) {
    telsToUse = R.filter(({ guid }) => G.notEquals(guid, telGuid), tels);
  }

  return R.compose(
    R.filter(G.isLoadTypeClo),
    R.reduce((acc, { items }) => R.concat(acc, items), []),
    R.filter((stop) => R.and(
      R.propEq(stopId, GC.FIELD_STOP_ID, stop),
      R.propEq(type, GC.FIELD_EVENT_TYPE, stop),
    )),
    R.reduce((acc, { events }) => R.concat(acc, R.values(events)), []),
  )(telsToUse);
};

export const getAvailableTerminalPickupClos = (tel, tels, stopId) => {
  const telGuid = G.getGuidFromObject(tel);

  const terminalDropedItems = getTerminalCloItemsByType(tels, telGuid, stopId, GC.EVENT_TYPE_DROP);
  const terminalPickedItems = getTerminalCloItemsByType(tels, telGuid, stopId, GC.EVENT_TYPE_PICKUP);
  const availableTerminalItems = filterPickedDroppedItems(terminalDropedItems, terminalPickedItems);

  const pickedTelItems = getTelCloItemsByType(tel, GC.EVENT_TYPE_PICKUP);
  const droppedTelItems = getTelCloItemsByType(tel, GC.EVENT_TYPE_DROP);
  const availableTelItems = filterPickedDroppedItems(droppedTelItems, pickedTelItems);

  const availableItems = R.compose(
    R.values,
    R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
    R.concat(availableTelItems),
  )(availableTerminalItems);

  if (G.isNilOrEmpty(availableItems)) return null;

  const clos = {};

  availableItems.forEach((item) => {
    const cloGuid = R.path([GC.FIELD_LOAD_GUID], item);

    if (R.not(R.has(cloGuid, clos))) clos[cloGuid] = [];

    clos[cloGuid].push(item);
  });

  return clos;
};

export const getAvailableDropClosWithItems = (tel) => {
  const droppedItems = getCloItemsInTelByEventType(tel, GC.EVENT_TYPE_DROP);
  const pickedItems = getCloItemsInTelByEventType(tel, GC.EVENT_TYPE_PICKUP);
  const availableItems = filterPickedDroppedItems(pickedItems, droppedItems);

  if (G.isNilOrEmpty(availableItems)) return null;

  const clos = {};
  availableItems.forEach((item) => {
    const cloGuid = R.path([GC.FIELD_LOAD_GUID], item);

    if (R.not(R.has(cloGuid, clos))) clos[cloGuid] = [];

    clos[cloGuid].push(item);
  });

  return clos;
};

export const getAvailableItemsForPickup = (stop, items) => (
  R.filter(
    (item) => G.isNilOrEmpty(
      R.find(
        (id) => R.equals(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item), id),
        stop.itemIds,
      ),
    ),
    items,
  )
);

export const getTelItemsForDrop = (props) => {
  const { tel, items } = props;
  const pickups = R.filter(
    (event) => R.and(
      R.not(R.path([GC.FIELD_CLO_EVENT], event)),
      R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE, event),
    ),
    R.values(tel[GC.FIELD_LOAD_STOPS]),
  );
  const drops = R.filter(
    (event) => R.and(
      R.not(R.path([GC.FIELD_CLO_EVENT], event)),
      R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE, event),
    ),
    R.values(tel[GC.FIELD_LOAD_STOPS]),
  );
  const pickedItemIds = R.reduce((acc, pickup) => R.concat(acc, pickup.itemIds), [], pickups);
  const droppedItemIds = R.reduce((acc, pickup) => R.concat(acc, pickup.itemIds), [], drops);
  const itemIds = R.difference(pickedItemIds, droppedItemIds);

  return R.compose(
    R.values,
    R.pick(itemIds),
    R.filter(({ loadType }) => R.not(G.isLoadTypeClo(loadType))),
  )(items);
};

const getItemsByEventTypeAndLoadGuid = (tels, dropStop, eventType) => R.compose(
  R.reduce((acc, stop) => R.concat(acc, stop.itemIds), []),
  R.filter((stop) => R.and(
    R.eqProps(GC.FIELD_LOAD_GUID, stop, dropStop),
    R.equals(eventType, stop.eventType),
  )),
  R.values,
  R.reduce((acc, { events }) => R.mergeRight(acc, events), {}),
  R.values,
)(tels);

export const getCloItemsForDrop = (props) => {
  const dropStop = props.stop;
  const tels = R.path(['currentRoute', GC.SYSTEM_LIST_TELS], props);
  const pickedItems = getItemsByEventTypeAndLoadGuid(tels, dropStop, GC.EVENT_TYPE_PICKUP);
  const droppedItems = getItemsByEventTypeAndLoadGuid(tels, dropStop, GC.EVENT_TYPE_DROP);
  const itemIds = R.difference(pickedItems, droppedItems);

  return R.map((id) => R.path([GC.FIELD_LOAD_ITEMS, id], props), itemIds);
};

export const getTerminalInitialDates = ({ dropTels, pickupTels } = {}, tels = {}) => {
  const currentDateTime = G.getCurrentDateWithFormat(GC.DEFAULT_DATE_TIME_FORMAT);
  let dates = {
    eventEarlyDate: currentDateTime,
    eventLateDate: G.addDateTimeWithFormat(currentDateTime, 8),
  };

  if (R.and(
    G.isNotNilAndNotEmpty(dropTels),
    G.isNotNilAndNotEmpty(tels[R.head(dropTels)]),
  )) {
    const lastPickup = R.compose(
      R.last,
      R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
      R.filter((event) => R.and(
        R.or(event.cloEvent, R.equals(R.prop(GC.FIELD_STOP_TYPE, event), GC.STOP_TYPE_TERMINAL)),
        R.equals(event.eventType, GC.EVENT_TYPE_PICKUP),
      )),
      R.values,
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tels[R.head(dropTels)]);

    if (G.isNotNilAndNotEmpty(lastPickup)) {
      const lastEventDate = R.or(G.getStopLateDate(lastPickup), G.getStopEarlyDate(lastPickup));
      dates = {
        eventEarlyDate: G.addDateTimeWithFormat(lastEventDate, 8),
        eventLateDate: G.addDateTimeWithFormat(lastEventDate, 16),
      };
    }
  }

  return dates;
};

export const createNewRouteTel = (options) => (
  R.mergeRight(
    {
      rate: null,
      events: {},
      isNew: true,
      linkedCloGuids: [],
      guid: G.generateGuid(),
      loadType: GC.LOAD_TYPE_TEL,
    },
    options,
  )
);
// COMMON

// SAGA
export const getInitialReportFromResponse = (reports, guid, routeGuid) => {
  const defaultReport = R.or(R.find(R.pathEq(true, [GC.FIELD_REPORT_DEFAULT]))(reports), reports[0]);

  return {
    withSearchCriteria: G.ifElse(
      G.isNilOrEmpty(routeGuid),
      false,
      R.assoc(
        GC.FIELD_SEARCH_CRITERIA,
        R.of(Array, {
          dataType: 'string',
          operation: 'equal',
          stringValue: routeGuid,
          propertyName: 'route.guid',
        }),
        defaultReport,
      ),
    ),
    empty: defaultReport,
    report: defaultReport,
  };
};

export const getItemsByIds = (ids, items, isNewEvent, shouldOmitLoadGuid) => {
  let omitFields = [
    'isNew',
    GC.FIELD_SELECTED,
    GC.FIELD_TEL_GUID,
    GC.FIELD_CLO_GUID,
    GC.FIELD_BRANCH_GUID,
  ];

  if (isNewEvent) omitFields = R.append(GC.FIELD_EVENT_GUID, omitFields);

  if (shouldOmitLoadGuid) omitFields = R.append(GC.FIELD_LOAD_GUID, omitFields);

  return R.map((id) => {
    const item = items[id];

    if (G.isTrue(R.path(['isNew'], item))) {

      return R.omit([GC.FIELD_GUID, ...omitFields], item);
    }

    return R.omit(omitFields, item);
  }, ids);
};

export const getEventWithItems = (
  event,
  currentRouteItems,
  shouldOmitLoadGuid = false,
) => R.assoc(
  GC.FIELD_LOAD_ITEMS,
  getItemsByIds(event.itemIds, currentRouteItems, R.prop('isNew', event), shouldOmitLoadGuid),
  event,
);

const getContainers = (ids, containers) => R.map(
  (id) => G.mapObjectEmptyStringFieldsToNull(R.prop(id, containers)),
  ids,
);

const getEventWithContainers = (
  event,
  containers,
) => R.mergeRight(
  event,
  {
    droppedContainers: getContainers(R.prop(GC.FIELD_STOP_DROPPED_CONTAINER_IDS, event), containers),
    pickedUpContainers: getContainers(R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event), containers),
  },
);

export const transformEventsForRequest = (
  tel,
  currentRouteItems,
  currentRouteContainers,
) => R.assoc(
  GC.FIELD_LOAD_STOPS,
  R.map(
    (stop) => {
      const event = G.formatStopDateTimeValues(G.mapObjectEmptyStringFieldsToNull(stop));
      const isNewTel = G.isTrue(R.prop('isNew', tel));
      const pathToTemplateId = [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_ID];
      const pathToOperationHour = [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_OPERATION_HOUR];

      const eventWithOperationHour = R.assocPath(
        pathToOperationHour,
        R.compose(
          G.convertOperationHoursToDefaultFormat,
          R.pathOr([], pathToOperationHour),
        )(event),
        G.setStringValueInsteadObjectValueByPath(event, pathToTemplateId, GC.FIELD_VALUE),
      );

      const shouldOmitLoadGuid = R.and(isNewTel, R.propEq(GC.STOP_TYPE_INDEPENDENT, GC.FIELD_STOP_TYPE, event));

      const eventWithContainers = getEventWithContainers(
        eventWithOperationHour,
        currentRouteContainers,
      );

      let eventWithItems = getEventWithItems(eventWithContainers, currentRouteItems, shouldOmitLoadGuid);

      if (isNewTel) {
        eventWithItems = R.omit([GC.FIELD_TEL_GUID, C.ORIGINAL_ROUTE_GUID], eventWithItems);
      }

      const path = [GC.FIELD_LOCATION, GC.FIELD_APPOINTMENT_CONTACT];

      eventWithItems = R.assocPath(
        path,
        G.omitObjectSystemFields(R.path(path, eventWithItems)),
        eventWithItems,
      );

      if (G.isTrue(R.prop('isNew', eventWithItems))) {

        return R.omit(['isNew', GC.FIELD_GUID, C.ORIGINAL_ROUTE_GUID], eventWithItems);
      }

      return R.omit([C.ORIGINAL_ROUTE_GUID], eventWithItems);
    },
    R.values(tel[GC.FIELD_LOAD_STOPS]),
  ),
  tel,
);

export const transformTelRateForRequest = (tel, initRate) => {
  const rate = R.path([GC.FIELD_RATE], tel);

  if (R.equals(rate, initRate)) return R.omit([GC.FIELD_RATE], tel);

  if (G.isNilOrEmpty(rate)) return tel;

  if (G.isNotNilAndNotEmpty(R.prop(GC.SYSTEM_OBJECT_CARRIER_ASSIGNMENT, rate))) {

    return G.renameKeys({ rate: 'carrierRate' }, tel);
  }

  if (G.isNilOrEmpty(R.prop(GC.SYSTEM_OBJECT_FLEET_ASSIGNMENT, rate))) {

    return R.assoc(
      'fleetRate',
      R.assoc(
        GC.SYSTEM_OBJECT_FLEET_ASSIGNMENT,
        R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, rate),
        rate,
      ),
      R.omit(GC.FIELD_RATE, tel),
    );
  }

  return G.renameKeys({ rate: 'fleetRate' }, tel);
};

const getPrimaryReferenceValue = (telWithRate, telPrimaryRefAuto, primaryReferenceTypeGuid) => {
  if (R.and(G.isTrue(telPrimaryRefAuto), G.isNilOrEmpty(primaryReferenceTypeGuid))) return null;

  return R.path([GC.FIELD_PRIMARY_REFERENCE, GC.FIELD_VALUE], telWithRate);
};

export const transformTelsForRequest = (
  route,
  currentRouteItems,
  telPrimaryRefAuto,
  initialRoute,
  currentRouteContainers,
) => R.assoc(
  GC.SYSTEM_LIST_TELS,
  R.compose(
    R.map((tel) => {
      const telWithEvents = transformEventsForRequest(tel, currentRouteItems, currentRouteContainers);
      const telWithRate = transformTelRateForRequest(
        telWithEvents,
        R.path([GC.SYSTEM_LIST_TELS, G.getGuidFromObject(tel), GC.FIELD_RATE], initialRoute),
      );
      const primaryReferenceTypeGuid = R.path([GC.FIELD_PRIMARY_REFERENCE, GC.FIELD_REFERENCE_TYPE_GUID], telWithRate);
      const telWithPrimaryReference = R.mergeRight(
        telWithRate,
        {
          primaryReferenceTypeGuid,
          [C.TEL_INDEX_IN_ROUTE]: tel.order,
          primaryReferenceValue: getPrimaryReferenceValue(telWithRate, telPrimaryRefAuto, primaryReferenceTypeGuid),
        },
      );

      if (G.isTrue(R.prop('isNew', telWithPrimaryReference))) {

        return R.omit([GC.FIELD_GUID, C.ORIGINAL_ROUTE_GUID, C.ORIGINAL_ROUTE_NAME], telWithPrimaryReference);
      }

      return R.omit([C.ORIGINAL_ROUTE_GUID, C.ORIGINAL_ROUTE_NAME], telWithPrimaryReference);
    }),
    R.filter((tel) => G.isNotNilAndNotEmpty(R.values(tel[GC.FIELD_LOAD_STOPS]))),
  )(R.values(route.tels)),
  route,
);

export const setTelEventItemsForRateAssignRequest = (telEvents, currentRouteItems) => R.map(
  (event) => getEventWithItems(event, currentRouteItems),
  telEvents,
);

export const transformCurrentRouteForRequest = (
  route,
  currentRouteItems,
  telPrimaryRefAuto,
  initialRoute,
  currentRouteContainers,
) => (
  R.omit(
    ['isNew', GC.SYSTEM_LIST_CLOS],
    transformTelsForRequest(route, currentRouteItems, telPrimaryRefAuto, initialRoute, currentRouteContainers),
  )
);

export const mapDraftsForInternalUsage = (data, startIndex) => (
  data.map((draft, index) => R.mergeRight(
    draft,
    {
      internalId: `draft-${R.add(startIndex, index)}`,
      details: { tels: draft.tels },
    },
  ))
);

const keysMap = {
  distance: GC.FIELD_DISTANCE_SYSTEM,
  unit: GC.FIELD_DISTANCE_SYSTEM_UOM,
};

const emptyDistance = {
  [GC.FIELD_DISTANCE_SYSTEM]: null,
  [GC.FIELD_DISTANCE_SYSTEM_UOM]: null,
};

export const mapLoadEventsWithDistances = (
  load,
  loadType,
  distanceRes,
) => {
  const distancesArr = R.compose(
    R.append(emptyDistance),
    R.map((item) => G.renameKeys(keysMap, item)),
    R.map((item) => R.pick([GC.FIELD_DISTANCE, 'unit'], item)),
    R.prop('stopResults'),
  )(distanceRes);
  const events = R.compose(
    R.sortBy(R.prop(G.getEventIndexPropByLoadType(loadType))),
    R.values(),
    R.prop(GC.FIELD_LOAD_STOPS),
  )(load);
  const zippedEvents = R.zipWith((event, distance) => {
    const distanceToNextStop = R.prop(G.getDistancePropByLoadType(loadType), event);
    const newDistanceToNextStop = R.mergeRight(distanceToNextStop, distance);

    return R.assoc(G.getDistancePropByLoadType(loadType), newDistanceToNextStop, event);
  }, events, distancesArr);

  return R.indexBy(R.prop(GC.FIELD_GUID), zippedEvents);
};

export const mapRouteItems = (tels = []) => {
  const items = R.compose(
    R.map((item) => R.mergeRight(item, {
      isNew: false,
      selected: true,
    })),
    R.uniqBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
  )(concatArrayItemsProp(concatArrayItemsProp(tels, GC.FIELD_LOAD_STOPS), GC.FIELD_LOAD_ITEMS));

  return R.indexBy(
    R.prop(GC.FIELD_ITEM_INTERNAL_ID),
    items,
  );
};

export const mapRouteContainers = (tels = []) => R.compose(
  R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
  R.map((item) => R.mergeRight(item, {
    isNew: false,
    selected: true,
  })),
  R.uniqBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
)(concatArrayItemsProp(concatArrayItemsProp(tels, GC.FIELD_LOAD_STOPS), GC.FIELD_STOP_PICKED_UP_CONTAINERS));

const extendDataWithConvertedTels = (data, tels) => (
  R.mergeRight(
    data,
    {
      clos: R.indexBy(
        R.prop(GC.FIELD_GUID),
        R.map(
          (clo) => R.assoc('loadName', getLoadName(clo), clo),
          R.or(data.clos, []),
        ),
      ),
      tels: R.indexBy(
        R.prop(GC.FIELD_GUID),
        tels,
      ),
    },
  )
);

const extendRouteTel = (tel, index) => R.mergeRight(
  tel,
  {
    loadName: G.ifElse(
      G.isNotNilAndNotEmpty(R.prop(GC.FIELD_PRIMARY_REFERENCE_VALUE, tel)),
      tel.primaryReferenceValue,
      getLoadName(tel),
    ),
    order: R.or(tel.order, index),
  },
);

export const convertRouteResponse = (data, indexAdditional) => {
  const mapIndexed = R.addIndex(R.map);
  const telsWithIndexedEvents = mapIndexed(
    (tel, index) => indexObjectArrayPropByPropName(
      GC.FIELD_GUID,
      GC.FIELD_LOAD_STOPS,
      extendRouteTel(tel, R.add(index, indexAdditional)),
    ),
    data.tels,
  );

  return extendDataWithConvertedTels(data, telsWithIndexedEvents);
};

export const convertRouteResponseWithOrigRouteGuid = (data, indexAdditional) => {
  const extendedData = R.mergeRight(data, {
    linkedClos: G.indexByGuid(data.linkedClos),
    [GC.SYSTEM_LIST_TELS]: R.map(
      (tel) => R.mergeRight(
        tel,
        {
          [C.ORIGINAL_ROUTE_GUID]: data[GC.FIELD_GUID],
          [C.ORIGINAL_ROUTE_NAME]: data[GC.FIELD_NAME],
          [GC.FIELD_LOAD_STOPS]: R.map(
            R.assoc(C.ORIGINAL_ROUTE_GUID, data.guid),
            tel[GC.FIELD_LOAD_STOPS],
          ),
        },
      ),
      data[GC.SYSTEM_LIST_TELS],
    ),
  });

  return convertRouteResponse(extendedData, R.or(indexAdditional, 0));
};

const mapDraftTels = (tels) => R.map(
  (tel) => {
    const telWithSortedEvents = R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.sortBy(
        R.prop(GC.FIELD_TEL_EVENT_INDEX),
        tel[GC.FIELD_LOAD_STOPS],
      ),
      tel,
    );

    if (G.isNotNilAndNotEmpty(R.prop(GC.FIELD_GUID, telWithSortedEvents))) return telWithSortedEvents;

    return R.mergeRight(telWithSortedEvents, {
      isNew: true,
      guid: G.generateGuid(),
    });
  },
  tels,
);

const extendDraftTel = (tel, index) => (
  R.mergeRight(
    tel,
    {
      loadType: GC.FIELD_TEL,
      loadName: G.ifElse(
        G.isNotNilAndNotEmpty(R.prop(GC.FIELD_PRIMARY_REFERENCE_VALUE, tel)),
        tel.primaryReferenceValue,
        getLoadName(tel),
      ),
      order: R.or(tel.order, index),
      events: tel[GC.FIELD_LOAD_STOPS].map((event) => {
        if (G.isNotNilAndNotEmpty(R.prop(GC.FIELD_GUID, event))) {

          return R.mergeRight(event, {
            telGuid: tel.guid,
          });
        }

        return R.mergeRight(event, {
          isNew: true,
          telGuid: tel.guid,
          guid: G.generateGuid(),
        });
      }),
    },
  )
);

export const convertDraftRouteResponse = (data) => {
  const tels = mapDraftTels(data.tels);
  const telsWithIndexedEvents = tels.map((tel, index) => indexObjectArrayPropByPropName(
      GC.FIELD_GUID,
      GC.FIELD_LOAD_STOPS,
      extendDraftTel(tel, index),
    ),
  );

  return extendDataWithConvertedTels(data, telsWithIndexedEvents);
};

export const getCurrentRouteTelsWithSplitEvents = ({ eventIndex, currentRoute, splittedLoadGuid }) => {
  const tels = R.pathOr({}, [GC.SYSTEM_LIST_TELS], currentRoute);
  const splittedTel = R.pathOr({}, [splittedLoadGuid], tels);

  const events = R.compose(
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    R.values,
    R.pathOr([], [GC.FIELD_LOAD_STOPS]),
  )(splittedTel);

  const indexToSlice = R.inc(eventIndex);

  const newTelEvents = events.slice(indexToSlice);
  const splittedTelEvents = events.slice(0, indexToSlice);

  const newTelGuid = G.generateGuid();
  const order = R.length(R.values(tels));

  const newTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.mergeRight(stop, {
      [GC.FIELD_TEL_GUID]: newTelGuid,
      [GC.FIELD_TEL_EVENT_INDEX]: R.inc(i),
      [GC.FIELD_LOAD_GUID]: G.ifElse(G.isLoadTypeTel(stop), newTelGuid, G.getPropFromObject(GC.FIELD_LOAD_GUID, stop)),
    })),
  )(newTelEvents);

  const telInfo = {
    order,
    [GC.FIELD_GUID]: newTelGuid,
    [GC.FIELD_LOAD_STOPS]: newTelStops,
    [GC.FIELD_BRANCH_GUID]: G.getBranchGuidFromObject(splittedTel),
    [GC.FIELD_PRIMARY_REFERENCE]: { [GC.FIELD_VALUE]: `${G.getWindowLocale('titles:tel', 'Trip')}-${R.inc(order)}` },
  };

  return {
    [newTelGuid]: createNewRouteTel(telInfo),
    [G.getGuidFromObject(splittedTel)]: R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.indexBy(R.prop(GC.FIELD_GUID), splittedTelEvents),
      splittedTel,
    ),
  };
};
// SAGA

// REDUCER
export const setSelectedPropIfExist = (tels, data) => {
  if (G.isNilOrEmpty(tels)) return tels;

  return tels.map((tel) => R.assoc(
    GC.FIELD_SELECTED,
    G.ifElse(R.includes(R.prop(GC.FIELD_GUID, tel), data), true, false),
    tel,
  ));
};

export const selectTelItems = (tels, ids) => (
  R.map((tel) => (
    P.$set(
      GC.FIELD_SELECTED,
      G.isNotNilAndNotEmpty(R.find(
        (id) => R.equals(R.prop(GC.FIELD_GUID, tel), id),
        ids,
      )),
      tel,
    )
  ), tels)
);

export const selectDraftItem = (list, id) => (
  R.map((item) => (
    P.$set(
      GC.FIELD_SELECTED,
      G.ifElse(
        R.equals(R.prop(GC.FIELD_INTERNAL_ID, item), id),
        R.not(R.prop(GC.FIELD_SELECTED, item)),
        false,
      ),
      item,
    )
  ), list)
);

export const selectItem = (list, id) => (
  R.map((item) => (
    P.$set(
      GC.FIELD_SELECTED,
      G.ifElse(
        R.equals(R.prop(GC.FIELD_GUID, item), id),
        R.not(R.prop(GC.FIELD_SELECTED, item)),
        R.prop(GC.FIELD_SELECTED, item),
      ),
      item,
    )
  ), list)
);

export const setItemSelected = (list, id, selected) => (
  R.map((item) => (
    R.equals(R.prop(GC.FIELD_GUID, item), id)
      ? R.assoc(GC.FIELD_SELECTED, selected, item)
      : item
  ), list)
);

export const hasCloEvents = (cloGuid, tels) => R.any(
  (tel) => R.any(
    (event) => R.equals(
      R.or(
        R.prop(GC.FIELD_CLO_GUID, event),
        G.isLoadTypeClo(event) ? R.prop(GC.FIELD_LOAD_GUID, event) : null,
      ),
      cloGuid,
    ),
    R.values(R.pathOr({}, [GC.FIELD_LOAD_STOPS], tel)),
  ),
  R.values(R.or(tels, {})),
);

export const filterCurrentRouteItems = (items, cloGuid) => R.compose(
  R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
  R.filter((item) => G.notEquals(item.loadGuid, cloGuid)),
  R.values,
)(items);

export const resetCloListToInitialRoute = (currentRoute, cloList) => (
  R.map(
    (clo) => R.assoc(
      GC.FIELD_SELECTED,
      G.isNotNilAndNotEmpty(R.path([GC.SYSTEM_LIST_CLOS, R.prop(GC.FIELD_GUID, clo)], currentRoute)),
      clo,
    ),
    cloList,
  )
);

export const createNewStopOnRouteTel = ({
  drop,
  pickup,
  telGuid,
  location,
  stopType,
  eventType,
  eventLateDate,
  telEventIndex,
  eventEarlyDate,
  appointmentDate,
  appointmentNumber,
  appointmentLateTime,
  appointmentEarlyTime,
}) => ({
  drop,
  pickup,
  telGuid,
  location,
  items: [],
  eventType,
  itemIds: [],
  isNew: true, // TODO: check it
  cloGuid: null,
  eventLateDate,
  telEventIndex,
  eventEarlyDate,
  comments: null,
  cloEvent: false,
  appointmentDate,
  completed: false,
  trailerGuids: [],
  appointmentNumber,
  cloEventIndex: null,
  appointmentLateTime,
  appointmentEarlyTime,
  guid: G.generateGuid(),
  eventCompleteDate: null,
  cloDistanceToNextStop: null,
  telDistanceToNextStop: null, // TODO: with Object
  stopType: R.or(stopType, GC.STOP_TYPE_INDEPENDENT),
  // containers
  [GC.FIELD_STOP_DROPPED_CONTAINERS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: [],
  [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: [],
  // trailers
  [GC.FIELD_STOP_DROPPED_TRAILERS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILERS]: [],
  [GC.FIELD_STOP_DROPPED_TRAILER_GUIDS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS]: [],
});

export const createNewTerminalOnRouteTel = (options) => {
  const newStop = createNewStopOnRouteTel(options);
  const guid = G.generateGuid();

  return {
    ...newStop,
    guid,
    stopId: options.stopId,
    stopType: R.or(options.stopType, GC.STOP_TYPE_TERMINAL),
  };
};

export const getNextTelEventIndex = (telGuid, state) => {
  const length = R.length(
    R.values(R.pathOr({}, ['currentRoute', GC.SYSTEM_LIST_TELS, telGuid, GC.FIELD_LOAD_STOPS], state),
  ));

  return R.add(length, 1);
};

export const omitLocationFields = (location) => R.omit(
  R.concat(GC.GROUPED_FIELDS.EVENT_DATES_ARR, GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR),
  R.mergeRight(location, {
    [GC.FIELD_CONTACTS]: R.map(
      R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR),
      R.pathOr([], [GC.FIELD_CONTACTS], location),
    ),
    [GC.FIELD_OPERATION_HOUR]: R.map(
      R.omit([GC.FIELD_GUID]),
      R.pathOr([], [GC.FIELD_OPERATION_HOUR], location),
    ),
  }),
);

export const generateDataForNewTelStopFromStopForm = (data, state) => {
  const telGuid = data.loadGuid;
  const eventType = data.eventType;
  const location = omitLocationFields(data.location);
  const drop = R.equals(data.eventType, GC.EVENT_TYPE_DROP);
  const telEventIndex = getNextTelEventIndex(telGuid, state);
  const pickup = R.equals(data.eventType, GC.EVENT_TYPE_PICKUP);
  const defaultDate = G.getCurrentDateWithFormat(GC.DEFAULT_DATE_TIME_FORMAT);
  const appointmentDate = R.pathOr(null, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_APPOINTMENT_DATE], data);
  const appointmentNumber = R.pathOr(null, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_APPOINTMENT_NUMBER], data);
  const eventEarlyDate = R.pathOr(defaultDate, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_EVENT_EARLY_DATE], data);
  const eventLateDate = R.pathOr(eventEarlyDate, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_EVENT_LATE_DATE], data);
  const appointmentLateTime = R.pathOr(null, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_APPOINTMENT_LATE_TIME], data);
  const appointmentEarlyTime = R.pathOr(null, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], data);

  return {
    drop,
    pickup,
    telGuid,
    location,
    eventType,
    telEventIndex,
    eventLateDate,
    eventEarlyDate,
    appointmentDate,
    appointmentNumber,
    appointmentLateTime,
    appointmentEarlyTime,
  };
};

export const createNewCloStopOnRouteTel = (options) => ({
  isNew: true,
  [GC.FIELD_STOP_ITEMS]: [],
  [GC.FIELD_COMMENTS]: null,
  [GC.FIELD_CLO_EVENT]: true,
  [GC.FIELD_STOP_TYPE]: null,
  [GC.FIELD_COMPLETED]: false,
  [GC.FIELD_STOP_ITEM_IDS]: [],
  [GC.FIELD_TRAILER_GUIDS]: [],
  [GC.FIELD_DISTANCE_CLO]: null,
  [GC.FIELD_DISTANCE_TEL]: null,
  [GC.FIELD_CLO_EVENT_INDEX]: null,
  [GC.FIELD_GUID]: G.generateGuid(),
  [GC.FIELD_EVENT_COMPLETE_DATE]: null,
  [GC.FIELD_LOAD_TYPE]: GC.LOAD_TYPE_CLO,
  [GC.FIELD_LOCATION]: R.prop(GC.FIELD_LOCATION, options),
  [GC.FIELD_CLO_GUID]: R.prop(GC.FIELD_CLO_GUID, options),
  [GC.FIELD_TEL_GUID]: R.prop(GC.FIELD_TEL_GUID, options),
  [GC.FIELD_LOAD_GUID]: R.prop(GC.FIELD_CLO_GUID, options),
  [GC.FIELD_STOP_DROP]: R.prop(GC.FIELD_STOP_DROP, options),
  [GC.FIELD_EVENT_TYPE]: R.prop(GC.FIELD_EVENT_TYPE, options),
  [GC.FIELD_STOP_PICKUP]: R.prop(GC.FIELD_STOP_PICKUP, options),
  [GC.FIELD_DROP_TRAILER]: R.prop(GC.FIELD_DROP_TRAILER, options),
  [GC.FIELD_TEL_EVENT_INDEX]: R.prop(GC.FIELD_TEL_EVENT_INDEX, options),
  [GC.FIELD_LOAD_EVENT_LATE_DATE]: R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, options),
  [GC.FIELD_LOAD_EVENT_EARLY_DATE]: R.prop(GC.FIELD_LOAD_EVENT_EARLY_DATE, options),
  [GC.FIELD_LOAD_APPOINTMENT_DATE]: R.prop(GC.FIELD_LOAD_APPOINTMENT_DATE, options),
  [GC.FIELD_LOAD_APPOINTMENT_NUMBER]: R.prop(GC.FIELD_LOAD_APPOINTMENT_NUMBER, options),
  [GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]: R.prop(GC.FIELD_LOAD_APPOINTMENT_LATE_TIME, options),
  [GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME]: R.prop(GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME, options),
  // containers
  [GC.FIELD_STOP_DROPPED_CONTAINERS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: [],
  [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: [],
  // trailers
  [GC.FIELD_STOP_DROPPED_TRAILERS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILERS]: [],
  [GC.FIELD_STOP_DROPPED_TRAILER_GUIDS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS]: [],
});

export const generateDataForNewCloStopFromStopForm = (data, state) => {
  const { telGuid, loadGuid, location, eventType } = data;

  const telEventIndex = getNextTelEventIndex(telGuid, state);
  const drop = G.isEventTypeDrop(eventType);
  const pickup = G.isEventTypePickup(eventType);
  const locationData = omitLocationFields(location);
  const dropTrailer = R.pathOr(false, [GC.FIELD_DROP_TRAILER], location);
  const eventEarlyDate = R.pathOr(null, [GC.FIELD_LOAD_EVENT_EARLY_DATE], location);
  const eventLateDate = R.pathOr(null, [GC.FIELD_LOAD_EVENT_LATE_DATE], location);
  const appointmentDate = R.pathOr(null, [GC.FIELD_LOAD_APPOINTMENT_DATE], location);
  const appointmentEarlyTime = R.pathOr(null, [GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], location);
  const appointmentLateTime = R.pathOr(null, [GC.FIELD_LOAD_APPOINTMENT_LATE_TIME], location);
  const appointmentNumber = R.pathOr(null, [GC.FIELD_LOAD_APPOINTMENT_NUMBER], location);

  return {
    drop,
    pickup,
    telGuid,
    loadGuid,
    eventType,
    dropTrailer,
    telEventIndex,
    eventLateDate,
    eventEarlyDate,
    appointmentDate,
    cloGuid: loadGuid,
    appointmentNumber,
    appointmentLateTime,
    appointmentEarlyTime,
    location: locationData,
  };
};

const getCloItemsFromStoreByEventTypeAndTelGuid = (eventType, telGuid, state) => R.compose(
  R.filter(G.isLoadTypeClo),
  R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_ITEMS, event)), []),
  R.filter((event) => R.propEq(eventType, GC.FIELD_EVENT_TYPE, event)),
  R.values,
  R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid, GC.FIELD_LOAD_STOPS]),
)(state);

const getItemsFromStoreByEventTypeAndTelGuid = (eventType, telGuid, state) => R.compose(
  R.reduce((acc, event) => R.concat(acc, event.items), []),
  R.filter(R.propEq(eventType, GC.FIELD_EVENT_TYPE)),
  R.values,
  R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid, GC.FIELD_LOAD_STOPS]),
)(state);

const filterItems = (items1 = [], items2 = []) => R.filter(
  (item) => R.isNil(R.find(R.eqProps(GC.FIELD_GUID, item), items2)),
  items1,
);

export const addTerminalDropsToCurrentRouteTels = (
  stopId,
  data,
  state,
) => {
  const dropTelsGuids = R.pathOr([], ['returnObject', 'dropTels'], data);
  const stopData = R.compose(
    R.assoc(GC.FIELD_EVENT_TYPE, GC.EVENT_TYPE_DROP),
    R.omit('returnObject'),
  )(data);
  const terminalDrops = R.compose(
    R.indexBy(R.prop(GC.FIELD_TEL_GUID)),
    R.map((telGuid) => {
      const stopDataWithTelGuid = R.assoc(GC.FIELD_LOAD_GUID, telGuid, stopData);
      const stopOptions = generateDataForNewTelStopFromStopForm(stopDataWithTelGuid, state);
      let newTerminal = createNewTerminalOnRouteTel(R.assoc(GC.FIELD_STOP_ID, stopId, stopOptions));
      const cloPickupItems = getCloItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_PICKUP, telGuid, state);
      const cloDropItems = getItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_DROP, telGuid, state);
      const availableItems = filterItems(cloPickupItems, cloDropItems);

      if (G.isNotNilAndNotEmpty(availableItems)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            items: availableItems,
            itemIds: R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), availableItems),
          },
        );
      }

      const availableContainers = getAvailableContainersForDrop(
        R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid], state),
      );

      if (G.isNotEmpty(availableContainers)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            [GC.FIELD_STOP_DROPPED_CONTAINERS]: availableContainers,
            [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), availableContainers),
          },
        );
      }

      return newTerminal;
    }),
  )(dropTelsGuids);
  const newCurrentRouteTels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.map((tel) => {
      const telGuid = R.prop(GC.FIELD_GUID, tel);

      if (R.includes(telGuid, dropTelsGuids)) {
        const terminalDrop = R.prop(telGuid, terminalDrops);

        return R.assocPath([GC.FIELD_LOAD_STOPS, R.prop(GC.FIELD_GUID, terminalDrop)], terminalDrop, tel);
      }

      return tel;
    }),
    R.values,
    R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
  )(state);

  return P.$set('currentRoute.tels', newCurrentRouteTels, state);
};

export const getTelAvailableContainersForPickup = (tel) => {
  const events = R.compose(
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);
  const droppedContainers = R.reduce(
    (acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, event)),
    [],
    events,
  );
  const pickedUpContainerIds = R.reduce(
    (acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event)),
    [],
    events,
  );

  return R.compose(
    R.values,
    R.omit(pickedUpContainerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
  )(droppedContainers);
};

export const getAvailableContainersForPickup = (stop, currentRoute) => {
  const terminalDropContainers = R.compose(
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, event)), []),
    R.filter((event) => R.and(
      R.eqProps(GC.FIELD_STOP_ID, event, stop),
      G.isStopDrop(event),
    )),
    R.values,
    R.reduce((acc, tel) => R.mergeRight(acc, R.prop(GC.FIELD_LOAD_STOPS, tel)), {}),
    R.values,
    R.prop(GC.SYSTEM_LIST_TELS),
  )(currentRoute);
  const terminalPickedContainerIds = R.compose(
    R.uniq,
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event))),
    R.filter((event) => R.and(
      R.eqProps(GC.FIELD_STOP_ID, event, stop),
      G.isStopPickup(event),
    )),
    R.values,
    R.reduce((acc, tel) => R.mergeRight(acc, R.prop(GC.FIELD_LOAD_STOPS, tel))),
    R.values,
    R.prop(GC.SYSTEM_LIST_TELS),
  )(currentRoute);

  return R.compose(
    R.values,
    R.omit(R.concat(terminalPickedContainerIds, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, stop))),
  )(terminalDropContainers);
};

const getAllStopsFromStateByStopId = (stopId, state) => R.compose(
  R.filter(R.propEq(stopId, GC.FIELD_STOP_ID)),
  R.values,
  R.reduce((acc, { events }) => R.mergeRight(acc, events), {}),
  R.values,
  R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
)(state);

const getAvailableTerminalPickupItems = (stopId, events) => {
  const droppedItems = R.compose(
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_ITEMS, event)), []),
    R.filter(R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE)),
  )(events);
  const pickedUpItemIds = R.compose(
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_ITEM_IDS, event)), []),
    R.filter(R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE)),
  )(events);

  return R.compose(
    R.values,
    R.omit(pickedUpItemIds),
    R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
  )(droppedItems);
};

const getAvailableTerminalPickupContainers = (stopId, events) => {
  const droppedContainers = R.reduce(
    (acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, event)),
    [],
    events,
  );
  const pickedUpContainerIds = R.reduce(
    (acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event)),
    [],
    events,
  );

  return R.compose(
    R.values,
    R.omit(pickedUpContainerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
  )(droppedContainers);
};

export const addTerminalPickupsToCurrentRouteTels = (
  stopId,
  data,
  state,
) => {
  const pickupTelsGuids = R.pathOr([], ['returnObject', 'pickupTels'], data);
  const singlePickup = R.equals(R.length(pickupTelsGuids), 1);
  const stopData = R.compose(
    R.assoc(GC.FIELD_EVENT_TYPE, GC.EVENT_TYPE_PICKUP),
    R.omit('returnObject'),
  )(data);
  const terminalPickups = R.compose(
    R.indexBy(R.prop(GC.FIELD_TEL_GUID)),
    R.map((telGuid) => {
      const stopDataWithTelGuid = R.assoc(GC.FIELD_LOAD_GUID, telGuid, stopData);
      const stopOptions = generateDataForNewTelStopFromStopForm(stopDataWithTelGuid, state);
      let newTerminal = createNewTerminalOnRouteTel(R.assoc(GC.FIELD_STOP_ID, stopId, stopOptions));
      let availableItems = [];
      let availableContainers = [];

      if (singlePickup) {
        const relatedStops = getAllStopsFromStateByStopId(stopId, state);
        availableItems = getAvailableTerminalPickupItems(stopId, relatedStops);
        availableContainers = getAvailableTerminalPickupContainers(stopId, relatedStops);
      } else {
        const cloDropItems = getCloItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_DROP, telGuid, state);
        const cloPickupItems = getItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_PICKUP, telGuid, state);
        availableItems = filterItems(cloDropItems, cloPickupItems);
        availableContainers = getTelAvailableContainersForPickup(
          R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid], state),
        );
      }

      if (G.isNotNilAndNotEmpty(availableItems)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            items: availableItems,
            itemIds: R.map((item) => item.itemInternalId, availableItems),
          },
        );
      }

      if (G.isNotEmpty(availableContainers)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: availableContainers,
            [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), availableContainers),
          },
        );
      }

      return newTerminal;
    }),
  )(pickupTelsGuids);
  const newCurrentRouteTels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.map((tel) => {
      const telGuid = R.prop(GC.FIELD_GUID, tel);

      if (R.includes(telGuid, pickupTelsGuids)) {
        const terminalPickup = R.prop(telGuid, terminalPickups);
        const newTel = R.assocPath([GC.FIELD_LOAD_STOPS, R.prop(GC.FIELD_GUID, terminalPickup)], terminalPickup, tel);
        const newTelEvents = R.compose(
          R.indexBy(R.prop(GC.FIELD_GUID)),
          R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
          R.map((event) => {
            if (R.equals(event.guid, terminalPickup.guid)) {

              return R.assoc(GC.FIELD_TEL_EVENT_INDEX, 1, event);
            }

            return R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(event.telEventIndex), event);
          }),
          R.values(),
          R.prop(GC.FIELD_LOAD_STOPS),
        )(newTel);

        return R.assoc(GC.FIELD_LOAD_STOPS, newTelEvents, newTel);
      }

      return tel;
    }),
    R.values(),
    R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
  )(state);

  return P.$set('currentRoute.tels', newCurrentRouteTels, state);
};

export const updateCurrentRouteTelsTerminalStops = (
  eventToUpdate,
  newLocation,
  state,
  eventGuid,
) => {
  const { stopType, stopId } = eventToUpdate;

  if (R.equals(stopType, GC.STOP_TYPE_TERMINAL)) {
    const newCurrentRouteTels = R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      R.map((tel) => {
        const newTelEvents = R.compose(
          R.indexBy(R.prop(GC.FIELD_GUID)),
          R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
          R.map((event) => {
            if (R.and(
              R.equals(stopId, R.prop(GC.FIELD_STOP_ID, event)),
              G.notEquals(G.getGuidFromObject(event), eventGuid),
            )) {
              const location = R.compose(
                R.mergeRight(newLocation),
                R.pick([GC.FIELD_GUID, GC.FIELD_VERSION, GC.FIELD_CONTACTS, GC.FIELD_COMMENTS]),
                R.prop(GC.FIELD_LOCATION),
              )(event);

              return R.assoc(GC.FIELD_LOCATION, location, event);
            }

            return event;
          }),
          R.values,
          R.prop(GC.FIELD_LOAD_STOPS),
        )(tel);

        return R.assoc(GC.FIELD_LOAD_STOPS, newTelEvents, tel);
      }),
      R.values(),
      R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
    )(state);

    return P.$set('currentRoute.tels', newCurrentRouteTels, state);
  }

  return state;
};

export const createTerminalStopWithExistedStopId = (options) => {
  const newStop = createNewStopOnRouteTel(options);

  return {
    ...newStop,
    stopId: options.stopId,
    stopType: GC.STOP_TYPE_TERMINAL,
  };
};

export const getFirstTerminalEventLocationByStopIdFromState = (stopId, state) => R.compose(
  R.prop(GC.FIELD_LOCATION),
  R.head(),
  R.reduce((acc, item) => R.concat(acc, item), []),
  R.map((tel) => R.compose(
    R.filter(R.propEq(stopId, GC.FIELD_STOP_ID)),
    R.values(),
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel)),
  R.values(),
  R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
)(state);

export const isEqualSourceAndDestinationLoad = (result) => (
  R.equals(
    R.path([GC.FIELD_SOURCE, 'droppableId'], result),
    R.path([GC.FIELD_DESTINATION, 'droppableId'], result),
  )
);

export const recreateTelEventsAfterDnD = R.compose(
  R.indexBy(R.prop(GC.FIELD_GUID)),
  R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
  G.mapIndexed((event, index) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(index), event)),
);

export const getIndexesFromDnDResult = (result) => {
  const sourceIndex = R.path([GC.FIELD_SOURCE, 'index'], result);
  const destinationIndex = R.path([GC.FIELD_DESTINATION, 'index'], result);

  const destinationIndexChecked = G.ifElse(
    R.equals(destinationIndex, -1),
    0,
    destinationIndex,
  );

  return {
    sourceIndex,
    destinationIndexChecked,
  };
};

export const reorderTelEvents = (
  sourceIndex,
  destinationIndex,
  events,
) => {
  const eventsArray = R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX), R.values(events));
  const [removedEvent] = eventsArray.splice(sourceIndex, 1);

  eventsArray.splice(destinationIndex, 0, removedEvent);

  return recreateTelEventsAfterDnD(eventsArray);
};

const timelineWindowFields = GC.GROUPED_FIELDS.EVENT_DATES_TIME_ARR;

const getTimelineTimestamp = (value) => {
  if (G.isNilOrEmpty(value)) return NaN;

  return new Date(value).getTime();
};

const getTimelineWindowSlot = (event, index) => {
  if (event[GC.FIELD_STOP_TYPE] === GC.STOP_TYPE_TERMINAL) return null;

  const earlyAt = getTimelineTimestamp(event[GC.FIELD_LOAD_EVENT_EARLY_DATE]);
  const lateAt = getTimelineTimestamp(event[GC.FIELD_LOAD_EVENT_LATE_DATE]);
  const startAt = Number.isFinite(earlyAt) ? earlyAt : lateAt;
  const endAt = Number.isFinite(lateAt) ? lateAt : earlyAt;

  if (!Number.isFinite(startAt) || !Number.isFinite(endAt) || endAt < startAt) return null;

  return {
    index,
    startAt,
    eventGuid: G.getGuidFromObject(event),
    fields: R.pick(timelineWindowFields, event),
  };
};

const getTimelineWindowSlots = R.compose(
  R.sortWith([
    R.ascend(R.prop('startAt')),
    R.ascend(R.prop('index')),
  ]),
  R.filter(Boolean),
  G.mapIndexed(getTimelineWindowSlot),
);

const reorderTelEventsWithTimelineWindows = (sourceIndex, destinationIndex, events) => {
  const firstAffectedIndex = Math.min(sourceIndex, destinationIndex);
  const lastAffectedIndex = Math.max(sourceIndex, destinationIndex);
  const orderedEvents = R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX), R.values(events));
  const reorderedEvents = R.values(reorderTelEvents(sourceIndex, destinationIndex, events));

  const timelineSlots = getTimelineWindowSlots(
    R.slice(firstAffectedIndex, R.inc(lastAffectedIndex), orderedEvents),
  );

  const scheduledEventGuids = new Set(timelineSlots.map(R.prop('eventGuid')));
  let slotIndex = 0;

  const eventsWithTimelineWindows = reorderedEvents.map((event, index) => {
    const eventGuid = G.getGuidFromObject(event);
    const affected = index >= firstAffectedIndex && index <= lastAffectedIndex;

    if (!affected || !scheduledEventGuids.has(eventGuid)) return event;

    const timelineSlot = timelineSlots[slotIndex];

    slotIndex += 1;

    return R.mergeRight(
      R.omit(timelineWindowFields, event),
      timelineSlot.fields,
    );
  });

  return recreateTelEventsAfterDnD(eventsWithTimelineWindows);
};

const reorderTimelineEventsByGuid = (events, timelineMove) => {
  const { sourceEventGuid, targetEventGuid } = timelineMove;

  const orderedEvents = R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX), R.values(events));
  const timelineSlots = getTimelineWindowSlots(orderedEvents);
  const eventsByGuid = R.indexBy(G.getGuidFromObject, orderedEvents);
  const timelineEvents = timelineSlots.map(({ eventGuid }) => eventsByGuid[eventGuid]);
  const sourceIndex = timelineSlots.findIndex(R.propEq(sourceEventGuid, 'eventGuid'));

  const targetIndex = targetEventGuid
    ? timelineSlots.findIndex(R.propEq(targetEventGuid, 'eventGuid'))
    : R.dec(timelineSlots.length);

  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return events;

  const [movedEvent] = timelineEvents.splice(sourceIndex, 1);

  timelineEvents.splice(targetIndex, 0, movedEvent);

  const reassignedTimelineEvents = timelineEvents.map((event, index) => R.mergeRight(
    R.omit(timelineWindowFields, event),
    timelineSlots[index].fields,
  ));

  const scheduledEventGuids = new Set(timelineSlots.map(R.prop('eventGuid')));
  let timelineIndex = 0;

  const eventsWithTimelineOrder = orderedEvents.map((event) => {
    if (!scheduledEventGuids.has(G.getGuidFromObject(event))) return event;

    const timelineEvent = reassignedTimelineEvents[timelineIndex];

    timelineIndex += 1;

    return timelineEvent;
  });

  return recreateTelEventsAfterDnD(eventsWithTimelineOrder);
};

export const reorderTelEventsInState = (state, result) => {
  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);

  const sourceTelGuid = R.path([GC.FIELD_SOURCE, 'droppableId'], result);
  const telEventsState = R.path(['currentRoute', GC.SYSTEM_LIST_TELS, sourceTelGuid, GC.FIELD_LOAD_STOPS], state);

  let newTelEvents = reorderTelEvents(sourceIndex, destinationIndexChecked, telEventsState);

  if (result.timelineMove) {
    newTelEvents = reorderTimelineEventsByGuid(telEventsState, result.timelineMove);
  } else if (result.reassignTimelineWindows) {
    newTelEvents = reorderTelEventsWithTimelineWindows(sourceIndex, destinationIndexChecked, telEventsState);
  }

  return P.$set(
    `currentRoute.tels.${sourceTelGuid}.events`,
    newTelEvents,
    state,
  );
};

const getCloItemsFromEventsByEventType = (events, eventType) => R.compose(
  R.reduce((acc, event) => R.concat(acc, event.items), []),
  R.filter((event) => R.and(
    R.propEq(eventType, GC.FIELD_EVENT_TYPE, event),
    R.propEq(GC.LOAD_TYPE_CLO, GC.FIELD_LOAD_TYPE, event),
  )),
)(events);

const getContainerIdsFromItems = (items) => R.compose(
  R.uniq,
  R.filter(G.isNotNilAndNotEmpty),
  R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
)(items);

const getContainersFromEvents = (events, propName) => R.reduce(
  (acc, event) => R.concat(acc, R.pathOr([], [propName], event)),
  [],
  events,
);

const checkTelEventsItems = (telEvents, containers) => {
  const terminals = R.compose(
    R.map((terminal) => {
      const cloPickupItems = getCloItemsFromEventsByEventType(telEvents, GC.EVENT_TYPE_PICKUP);
      const cloDropItems = getCloItemsFromEventsByEventType(telEvents, GC.EVENT_TYPE_DROP);
      const pickedUpContainers = getContainersFromEvents(telEvents, GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS);
      const droppedContainers = getContainersFromEvents(telEvents, GC.FIELD_STOP_DROPPED_CONTAINER_IDS);
      let availableItems;
      let containersData = {};

      if (G.isStopPickup(terminal)) {
        availableItems = filterItems(cloDropItems, cloPickupItems);
        const terminalContainers = R.pathOr([], [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS], terminal);

        const containerIds = R.without(
          R.difference(terminalContainers, droppedContainers),
          terminalContainers,
        );

        containersData = {
          [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: containerIds,
          [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.values(R.pick(containerIds, containers)),
        };
      } else {
        availableItems = filterItems(cloPickupItems, cloDropItems);
        const terminalContainers = R.pathOr([], [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS], terminal);

        const containerIds = R.without(
          R.difference(terminalContainers, pickedUpContainers),
          terminalContainers,
        );
        containersData = {
          [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: containerIds,
          [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.values(R.pick(containerIds, containers)),
        };
      }

      return R.mergeRight(
        terminal,
        {
          ...containersData,
          items: availableItems,
          itemIds: R.map((item) => item.itemInternalId, availableItems),
        },
      );
    }),
    R.filter(R.propEq(GC.STOP_TYPE_TERMINAL, GC.FIELD_STOP_TYPE)),
  )(telEvents);

  if (R.isEmpty(terminals)) return telEvents;

  return R.map(
    (event) => {
      const existInTerminals = R.find(R.eqProps(GC.FIELD_GUID, event), terminals);

      if (existInTerminals) return existInTerminals;

      return event;
    },
    telEvents,
  );
};

export const moveEventFromTelToTel = (state, result) => {
  const sourceTelGuid = R.path([GC.FIELD_SOURCE, 'droppableId'], result);
  const destinationTelGuid = R.path([GC.FIELD_DESTINATION, 'droppableId'], result);
  const sourceTelEventsArray = R.values(R.path(
    ['currentRoute', GC.SYSTEM_LIST_TELS, sourceTelGuid, GC.FIELD_LOAD_STOPS],
    state,
  ));
  const destinationTelEventsArray = R.values(R.path(
    ['currentRoute', GC.SYSTEM_LIST_TELS, destinationTelGuid, GC.FIELD_LOAD_STOPS],
    state,
  ));
  const containers = R.path(['currentRouteContainers'], state);
  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);
  const [removedEvent] = sourceTelEventsArray.splice(sourceIndex, 1);
  destinationTelEventsArray.splice(destinationIndexChecked, 0, removedEvent);
  const sourceEvents = checkTelEventsItems(sourceTelEventsArray, containers);
  const destinationEvents = checkTelEventsItems(destinationTelEventsArray, containers);

  return P.$all(
    P.$set(
      `currentRoute.tels.${sourceTelGuid}.events`,
      recreateTelEventsAfterDnD(sourceEvents),
    ),
    P.$set(
      `currentRoute.tels.${destinationTelGuid}.events`,
      recreateTelEventsAfterDnD(R.map(
        R.assoc(GC.FIELD_TEL_GUID, destinationTelGuid),
        destinationEvents,
      )),
    ),
    state,
  );
};

export const mapCloDetailsEvents = (eventsCount, events, telGuid) => G.mapIndexed(
  (event, index) => R.assoc(
    GC.FIELD_TEL_GUID,
    telGuid,
    R.assoc(
      GC.FIELD_TEL_EVENT_INDEX,
      R.add(
        R.inc(index),
        eventsCount,
      ),
      event,
    ),
  ), events);

export const createNewEventsForTel = (cloDetails, tel) => {
  const telEvents = R.values(R.prop(GC.FIELD_LOAD_STOPS, tel));
  const telEventsCount = R.length(telEvents);
  const mappedCloEvents = mapCloDetailsEvents(
    telEventsCount,
    R.prop(GC.FIELD_LOAD_STOPS, cloDetails),
    tel.guid,
  );

  return R.indexBy(R.prop(GC.FIELD_GUID), R.concat(telEvents, mappedCloEvents));
};

const filterAndMergeTelEventItems = (telEvent, itemsToFilter) => R.mergeRight(telEvent, {
  itemIds: R.difference(telEvent.itemIds, itemsToFilter),
  items: R.filter(
    (item) => R.all(
      (id) => R.not(R.propEq(id, GC.FIELD_ITEM_INTERNAL_ID, item)),
      itemsToFilter,
    ),
    telEvent.items,
  ),
});

export const getTelsFilteredFromItems = (
  tels,
  stopTel,
  stop,
  itemsToFilter,
) => {
  const isCloStop = G.isLoadTypeClo(stop);
  const isCloPickup = R.and(isCloStop, G.isStopPickup(stop));

  if (isCloStop) {

    return R.map(
      (routeTel) => R.assoc(
        GC.FIELD_LOAD_STOPS,
        R.map(
          (telEvent) => {
            if (R.or(isCloPickup, R.eqProps(GC.FIELD_GUID, telEvent, stop))) {

              return filterAndMergeTelEventItems(telEvent, itemsToFilter);
            }

            return telEvent;
          },
          routeTel[GC.FIELD_LOAD_STOPS],
        ),
        routeTel,
      ),
      tels,
    );
  }

  return R.map(
    (routeTel) => {
      if (R.gte(routeTel.order, stopTel.order)) {

        return R.assoc(
          GC.FIELD_LOAD_STOPS,
          R.map(
            (telEvent) => {
              if (R.or(
                G.isLoadTypeClo(telEvent),
                R.and(
                  R.eqProps(GC.FIELD_GUID, routeTel, stopTel),
                  R.lt(telEvent.telEventIndex, stop.telEventIndex),
                ),

              )) return telEvent;

              return filterAndMergeTelEventItems(telEvent, itemsToFilter);
            },
            routeTel[GC.FIELD_LOAD_STOPS],
          ),
          routeTel,
        );
      }

      return routeTel;
    },
    tels,
  );
};

const filterTelEventContainers = (event, containerIds) => R.mergeRight(event, {
  [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.without(
    containerIds,
    R.prop(GC.FIELD_STOP_DROPPED_CONTAINER_IDS, event),
  ),
  [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.without(
    containerIds,
    R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event),
  ),
  [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.compose(
    R.values,
    R.omit(containerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS),
  )(event),
  [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.compose(
    R.values,
    R.omit(containerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS),
  )(event),
});

const filterEventItemsFromContainers = (event, containerIds) => R.assoc(
  GC.FIELD_LOAD_ITEMS,
  R.map(
    (item) => {
      const { containerInternalId } = item;

      if (R.includes(containerInternalId, containerIds)) {

        return R.mergeRight(item, {
          [GC.FIELD_CONTAINER_GUID]: null,
          [GC.FIELD_CONTAINER_INTERNAL_ID]: null,
        });
      }

      return item;
    },
    R.prop(GC.FIELD_LOAD_ITEMS, event),
  ),
  event,
);

export const getTelsFilteredFromContainers = (
  tels,
  stopTel,
  stop,
  isPickupContainer,
  containersToFilter,
) => {
  const isCloStop = G.isLoadTypeClo(stop);
  const isPickup = G.isStopPickup(stop);

  return R.map(
    (routeTel) => R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.map(
        (telEvent) => {
          const cond1 = R.and(isCloStop, isPickupContainer);
          const cond2 = R.eqProps(GC.FIELD_GUID, telEvent, stop);
          const cond3 = R.and(R.not(isPickup), R.not(G.isLoadTypeClo(telEvent)));

          if (G.isAnyTrue(cond1, cond2, cond3)) {
            const eventToUse = G.ifElse(
              cond1,
              filterEventItemsFromContainers(telEvent, containersToFilter),
              telEvent,
            );

            return filterTelEventContainers(eventToUse, containersToFilter);
          }

          return telEvent;
        },
        routeTel[GC.FIELD_LOAD_STOPS],
      ),
      routeTel,
    ),
    tels,
  );
};

export const removeCloItemsFromEvent = (event, cloGuid) => {
  const itemIds = R.compose(
    R.map((item) => item.itemInternalId),
    R.filter((item) => R.equals(item.loadGuid, cloGuid)),
  )(event.items);

  return R.mergeRight(
    event,
    {
      itemIds: R.without(itemIds, event.itemIds),
      items: R.filter(
        (item) => G.notEquals(item.loadGuid, cloGuid),
        event.items,
      ),
    },
  );
};

export const removeCloEventsFromTels = (cloGuid, tels) => (
  R.mapObjIndexed((tel) => (
    R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.indexBy(
        R.prop(GC.FIELD_GUID),
        sortTelEvents(
          R.filter((event) => R.not(R.equals(event.cloGuid, cloGuid)), tel[GC.FIELD_LOAD_STOPS]),
        ).map(
          (event, index) => R.assoc(
            GC.FIELD_TEL_EVENT_INDEX,
            R.inc(index),
            removeCloItemsFromEvent(event, cloGuid),
          ),
        ),
      ),
      tel,
    )
  ), tels)
);

const removeRouteEventsFromTels = (routeGuid, tels) => R.compose(
  R.indexBy(R.prop(GC.FIELD_GUID)),
  R.filter((tel) => R.not(R.and(
    G.isTrue(tel.isNew),
    R.isEmpty(R.values(tel[GC.FIELD_LOAD_STOPS])),
  ))),
  G.mapIndexed(
    (tel, i) => {
      let newTel = R.assoc(
        GC.FIELD_LOAD_STOPS,
        R.indexBy(
          R.prop(GC.FIELD_GUID),
          sortTelEvents(R.filter(
            (event) => R.not(R.equals(event[C.ORIGINAL_ROUTE_GUID], routeGuid)),
            tel[GC.FIELD_LOAD_STOPS],
          )).map((event, index) => R.assoc(
            GC.FIELD_TEL_EVENT_INDEX,
            R.inc(index),
            event,
          )),
        ),
        tel,
      );

      if (R.equals(tel[C.ORIGINAL_ROUTE_GUID], routeGuid)) {
        newTel = R.mergeRight(
          R.omit([C.ORIGINAL_ROUTE_GUID, C.ORIGINAL_ROUTE_NAME], newTel),
          {
            isNew: true,
            loadName: `TEL-${R.inc(i)}`,
            primaryReference: { value: `TEL-${R.inc(i)}` },
          },
        );
      }

      return newTel;
    },
  ),
  R.values,
)(tels);

export const removeTelFromPlanner = (state, guid) => {
  const { currentRoute } = state;
  const routeGuid = R.path([GC.SYSTEM_LIST_TELS, guid, C.ORIGINAL_ROUTE_GUID], currentRoute);
  let closToRemove = [];
  let telsToRemove = [];
  R.values(currentRoute[GC.SYSTEM_LIST_TELS]).forEach((tel) => {
    if (R.equals(tel[C.ORIGINAL_ROUTE_GUID], routeGuid)) telsToRemove = R.append(tel[GC.FIELD_GUID], telsToRemove);

    R.values(tel[GC.FIELD_LOAD_STOPS]).forEach((event) => {
      if (R.equals(routeGuid, event[C.ORIGINAL_ROUTE_GUID])) {

        if (G.isLoadTypeClo(event)) {
          closToRemove = R.append(event[GC.FIELD_LOAD_GUID], closToRemove);
        }
      }
    });
  });
  const selectedTels = R.omit(telsToRemove, state.selectedTels);
  const telList = setSelectedPropIfExist(state.telList, R.values(selectedTels));
  const inboundTelList = setSelectedPropIfExist(state.inboundTelList, R.values(selectedTels));
  const outboundTelList = setSelectedPropIfExist(state.outboundTelList, R.values(selectedTels));
  closToRemove = R.uniq(closToRemove);
  const clos = R.omit(closToRemove, state.currentRoute[GC.SYSTEM_LIST_CLOS]);
  const cloList = R.map(
    (clo) => R.assoc(
      GC.FIELD_SELECTED,
      G.ifElse(
        R.includes(clo[GC.FIELD_GUID], closToRemove),
        false,
        R.prop(GC.FIELD_SELECTED, clo),
      ),
      clo,
    ),
    state.cloList,
  );
  const currentRouteItems = R.compose(
    R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
    R.filter((item) => G.notContain(item.loadGuid, closToRemove)),
    R.values,
  )(state.currentRouteItems);
  let tels = currentRoute[GC.SYSTEM_LIST_TELS];
  closToRemove.forEach((cloGuid) => {
    tels = removeCloEventsFromTels(cloGuid, tels);
  });
  tels = removeRouteEventsFromTels(routeGuid, tels);

  return R.mergeRight(
    state,
    {
      cloList,
      telList,
      selectedTels,
      inboundTelList,
      outboundTelList,
      currentRouteItems,
      currentRoute: R.mergeRight(currentRoute, { clos, tels }),
    },
  );
};

export const getUpdatedStopOnAddItem = ({ stop, state, loadItems, currentItems }) => {
  const items = R.uniqBy(R.prop(GC.FIELD_GUID), loadItems);
  const itemIds = R.compose(
    R.uniq,
    R.concat(R.pathOr(
      [],
      ['currentRoute', GC.SYSTEM_LIST_TELS, stop.telGuid, GC.FIELD_LOAD_STOPS, stop.guid, GC.FIELD_STOP_ITEM_IDS],
      state,
    )),
    R.concat(currentItems),
    R.map((loadItem) => loadItem.itemInternalId),
  )(loadItems);

  if (G.isStopTypeTerminal(stop.stopType)) {
    const containers = state.currentRouteContainers;
    let containerIds = getContainerIdsFromItems(items);

    if (G.isStopPickup(stop)) {
      containerIds = R.compose(
        R.uniq,
        R.concat(R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, stop)),
      )(containerIds);

      return R.mergeRight(stop, {
        items,
        itemIds,
        [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: containerIds,
        [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.values(R.pick(containerIds, containers)),
      });
    }

    containerIds = R.compose(
      R.uniq,
      R.concat(R.prop(GC.FIELD_STOP_DROPPED_CONTAINER_IDS, stop)),
    )(containerIds);

    return R.mergeRight(stop, {
      items,
      itemIds,
      [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: containerIds,
      [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.values(R.pick(containerIds, containers)),
    });
  }

  return R.mergeRight(stop, { items, itemIds });
};
// REDUCER

// SELECTORS
export const getRouteTotalDistance = (loads, distanceName) => {
  const loadsDistances = R.map(
    (load) => G.getLoadTotalDistance(load, distanceName),
    R.values(loads),
  );
  const totalTripDistance = R.sum(R.map(R.prop(GC.FIELD_TOTAL_TRIP_DISTANCE), loadsDistances));
  const totalTripDistanceUom = G.ifElse(
    R.equals(NaN, totalTripDistance),
    '',
    R.pathOr(GC.UOM_MILE, [0, GC.FIELD_TOTAL_TRIP_DISTANCE_UOM], loadsDistances),
  );

  return {
    totalTripDistanceUom,
    totalTripDistance: G.ifElse(
      R.equals(NaN, totalTripDistance),
      'N/A',
      totalTripDistance.toFixed(2),
    ),
  };
};
// SELECTORS

// UI
export const getTelTotalInfo = (tel, items) => {
  if (G.isNilOrEmpty(tel[GC.FIELD_LOAD_STOPS])) return false;

  const itemIds = R.uniq(R.reduce(
    R.concat,
    [],
    R.map(
      (event) => event.itemIds,
      R.values(tel[GC.FIELD_LOAD_STOPS]),
    ),
  ));
  const telItems = R.filter(
    (item) => G.isNotNilAndNotEmpty(item),
    R.map(
      (id) => R.prop(id, items),
      itemIds,
    ),
  );

  if (G.isNilOrEmpty(telItems)) return false;

  return {
    itemsTotal: telItems.length,
    totalQuantity: G.calculateTotalQuantity(telItems),
    palletSpaces: G.calculateItemsPalletSpaces(telItems),
    totalWeight: G.calcItemsTotalWeightWithoutQty(telItems, false),
    totalDistance: G.getLoadTotalDistance(tel, GC.FIELD_DISTANCE_TEL),
    totalBillableWeight: R.any(R.prop(GC.FIELD_BILLABLE_WEIGHT), telItems)
      ? G.calcItemsTotalWeightWithoutQty(telItems)
      : {},
  };
};

export const getStopItemsCount = (stop = {}) => {
  if (R.or(
    G.isTrue(R.path([GC.FIELD_CLO_EVENT], stop)),
    G.notEquals(R.path([GC.FIELD_STOP_TYPE], stop), GC.STOP_TYPE_TERMINAL),
  )) {

    return R.length(stop.itemIds);
  }

  return R.compose(
    R.add(R.length(stop.items)),
    R.length,
    R.filter(
      (id) => G.isNilOrEmpty(R.find(
        ({ itemInternalId }) => R.equals(itemInternalId, id),
        stop.items,
      )),
    ),
  )(stop.itemIds);
};

export const checkTelExpandedOnPlanner = (stops, mode, visibility) => {
  if (G.isNotNilAndNotEmpty(visibility)) {

    return G.ifElse(R.equals(visibility, 'visible'), true, false);
  }

  if (R.equals(mode, C.PLANNER_TEL_MODES.ALL_EXPANDED)) return true;

  if (R.equals(mode, C.PLANNER_TEL_MODES.ALL_COLLAPSED)) return false;

  const typeMap = {
    [C.PLANNER_TEL_MODES.INBOUND]: GC.EVENT_TYPE_DROP,
    [C.PLANNER_TEL_MODES.OUTBOUND]: GC.EVENT_TYPE_PICKUP,
  };
  const terminals = R.filter(
    (stop) => R.equals(stop.stopType, GC.STOP_TYPE_TERMINAL),
    stops,
  );

  return R.all(
    R.propEq(typeMap[mode], GC.FIELD_EVENT_TYPE),
    terminals,
  );
};

export const getAvailableListHeight = ({
  plannerView,
  renderedListHeight,
}) => {
  if (R.includes(plannerView, [C.COLLAPSED, C.STICKED_RIGHT])) {

    return Math.max(0, renderedListHeight - C.TOOLBAR_HEIGHT);
  }

  return Math.max(0, renderedListHeight);
};

const normalizePlannerUISettings = (settings) => {
  if (G.notEquals(R.type(settings), 'Object')) return {};

  const plannerView = R.prop('plannerView', settings);
  const scheduleMapOpen = R.prop('scheduleMapOpen', settings);
  const scheduleEditorOn = R.prop('scheduleEditorOn', settings);
  const paneSizes = R.pick(['contentOneSize', 'stickToRightWidth'], settings);

  const isValidPlannerView = R.includes(plannerView, [
    C.COLLAPSED,
    C.HALF_SCREEN,
    C.FULL_SCREEN,
    C.STICKED_RIGHT,
  ]);

  const validPaneSizes = R.pickBy(
    (size) => G.isNumber(size) && Number.isFinite(size) && size > 0,
    paneSizes,
  );

  return R.pickBy(G.isNotNil, {
    ...validPaneSizes,
    plannerView: isValidPlannerView ? plannerView : null,
    scheduleMapOpen: R.is(Boolean, scheduleMapOpen) ? scheduleMapOpen : null,
    scheduleEditorOn: R.is(Boolean, scheduleEditorOn) ? scheduleEditorOn : null,
  });
};

export const getPlannerUISettings = () => {
  try {
    const settings = JSON.parse(window.localStorage.getItem(C.PLANNER_UI_SETTINGS_STORAGE_KEY));

    return normalizePlannerUISettings(settings);
  } catch (error) { // eslint-disable-line no-unused-vars
    return {};
  }
};

export const getInitialPlannerView = (settings, splittedLoadOptions) => R.propOr(
  R.isEmpty(splittedLoadOptions) ? C.HALF_SCREEN : C.FULL_SCREEN,
  'plannerView',
  settings,
);

export const updatePlannerUISettings = (settings) => {
  const currentSettings = getPlannerUISettings();
  const nextSettings = normalizePlannerUISettings(R.mergeRight(currentSettings, settings));

  try {
    window.localStorage.setItem(
      C.PLANNER_UI_SETTINGS_STORAGE_KEY,
      JSON.stringify(nextSettings),
    );
  } catch (error) { // eslint-disable-line no-unused-vars
    return false;
  }

  return true;
};

export const getPlannerSplitSizeKey = (plannerView) => ({
  [C.HALF_SCREEN]: 'contentOneSize',
  [C.STICKED_RIGHT]: 'stickToRightWidth',
}[plannerView]);

export const updatePlannerSplitSize = (plannerView, contentOneSize) => {
  const sizeKey = getPlannerSplitSizeKey(plannerView);

  if (!sizeKey || !Number.isFinite(contentOneSize) || contentOneSize <= 0) return false;

  return updatePlannerUISettings({ [sizeKey]: contentOneSize });
};
// UI

// logic
const isWrongEventDate = (event, index, events) => {
  const eventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_DATE]),
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]),
    ),
    `${event[GC.FIELD_LOAD_APPOINTMENT_DATE]} ${event[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]}`,
    null,
  );
  const prevEventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events)),
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], events)),
    ),
    `${R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events)} ${
      R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], events)}`,
    null,
  );

  return R.and(
    R.gt(index, 0),
    G.isBeforeDate(
      R.or(eventAppointment, event[GC.FIELD_LOAD_EVENT_LATE_DATE]),
      R.or(prevEventAppointment, R.path([R.dec(index), GC.FIELD_LOAD_EVENT_EARLY_DATE], events)),
    ),
  );
};

const isWrongEventDateWithoutTime = (event, index, events) => {
  const eventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_DATE]),
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]),
    ),
    event[GC.FIELD_LOAD_APPOINTMENT_DATE],
    null,
  );

  const prevEventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events)),
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], events)),
    ),
    R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events),
    null,
  );

  return R.and(
    R.gt(index, 0),
    G.isBeforeDate(
      R.or(eventAppointment, G.convertInstanceToDefaultDateFormat(event[GC.FIELD_LOAD_EVENT_LATE_DATE])),
      R.or(
        prevEventAppointment,
        G.convertInstanceToDefaultDateFormat(R.path([R.dec(index), GC.FIELD_LOAD_EVENT_EARLY_DATE], events)),
      ),
    ),
  );
};

const getWrongPickupItems = (event, index, events) => {
  const dropItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_LOAD_ITEMS)),
    R.filter(R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE)),
    R.slice(R.inc(index), Infinity),
  )(events);

  return R.filter(
    (item) => G.isNilOrEmpty(R.find(
      R.eqProps(GC.FIELD_GUID, item),
      dropItems,
    )),
    R.prop(GC.FIELD_LOAD_ITEMS, event),
  );
};

const getWrongDropItems = (event, index, events) => {
  const pickupItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_STOP_ITEM_IDS)),
    R.filter(R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE)),
    R.take(index),
  )(events);

  return R.filter(
    (item) => G.isNilOrEmpty(R.find(
      R.equals(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item)),
      pickupItems,
    )),
    R.prop(GC.FIELD_LOAD_ITEMS, event),
  );
};

export const getEventsErrors = (events) => {
  let errors = {};
  events.forEach((event, index) => {
    const isPickup = G.isEventTypePickup(R.prop(GC.FIELD_EVENT_TYPE, event));

    const disableTimeValidation = G.getAmousConfigByNameFromWindow(GC.TEL_ROUTE_BUILDER_DISABLE_TIME_VALIDATION);

    const wrongDateFunction = disableTimeValidation ? isWrongEventDateWithoutTime : isWrongEventDate;

    const isWrongDate = wrongDateFunction(event, index, events);

    if (isWrongDate) {
      errors = R.assocPath([R.prop(GC.FIELD_GUID, event), GC.FIELD_DATE], true, errors);
    }

    const wrongItems = G.ifElse(
      isPickup,
      getWrongPickupItems,
      getWrongDropItems,
    )(event, index, events);

    if (G.isNotEmpty(wrongItems)) {
      errors = R.assocPath([R.prop(GC.FIELD_GUID, event), GC.FIELD_LOAD_ITEMS], wrongItems, errors);
    }

  });

  return errors;
};

export const getContainersError = (tel) => {
  const pickupContainers = getTelAvailableContainersForPickup(tel);
  const dropContainers = getAvailableContainersForDrop(tel);

  return R.or(G.isNotEmpty(pickupContainers), G.isNotEmpty(dropContainers));
};

const hasInvalidDistances = (events) => G.isNotNilAndNotEmpty(
  events.find((event, index) => (
    R.and(
      R.lt(index, R.dec(events.length)),
      G.isNilOrEmpty(event.telDistanceToNextStop),
    )
  )),
);

export const validateRoute = (currentRoute = {}) => {
  const errors = {};
  const tels = R.values(R.pathOr({}, [GC.SYSTEM_LIST_TELS], currentRoute));
  const noTels = R.isEmpty(tels);

  tels.forEach((tel) => {
    const telEvents = R.sortBy(
      R.prop(GC.FIELD_TEL_EVENT_INDEX),
      R.values(R.propOr({}, GC.FIELD_LOAD_STOPS, tel)),
    );

    const eventsErrors = getEventsErrors(telEvents);
    const distanceError = hasInvalidDistances(telEvents);
    const eventsCountError = R.lt(telEvents.length, 2);
    const containersError = getContainersError(tel);

    if (R.any(G.isTrue, [
      distanceError,
      containersError,
      eventsCountError,
      G.isNotEmpty(eventsErrors),
    ])) {
      errors[tel.guid] = {
        eventsErrors,
        distanceError,
        containersError,
        eventsCountError,
      };
    }
  });

  return {
    noTels,
    errors,
    isValid: R.and(R.not(noTels), R.isEmpty(errors)),
  };
};

const getValidationTel = (currentRoute, telGuid) => {
  const tels = R.pathOr({}, [GC.SYSTEM_LIST_TELS], currentRoute);

  return R.or(
    R.prop(telGuid, tels),
    R.find(R.propEq(telGuid, GC.FIELD_GUID), R.values(tels)),
  );
};

const createValidationIssue = (data) => ({
  scope: 'trip',
  ...data,
});

export const getRouteValidationIssues = (validation, currentRoute = {}) => {
  const issues = [];

  if (validation.noTels) {
    issues.push(createValidationIssue({
      scope: 'route',
      type: 'eventsCount',
      id: 'route-no-trips',
      message: G.getWindowLocale(
        'validation:route-should-contain-tel',
        'Route should contain at least one trip',
      ),
    }));
  }

  Object.entries(validation.errors || {}).forEach(([telGuid, telError]) => {
    const tel = getValidationTel(currentRoute, telGuid);
    const tripName = getLoadName(tel, G.getWindowLocale('titles:trip', 'Trip'));

    if (telError.eventsCountError) {
      issues.push(createValidationIssue({
        telGuid,
        tripName,
        type: 'eventsCount',
        message: G.getWindowLocale(
          'validation:tel-should-contain-min-two-stops',
          'Trip should contain at least two stops',
        ),
        id: `${telGuid}-events-count`,
      }));
    }

    if (telError.distanceError) {
      issues.push(createValidationIssue({
        telGuid,
        tripName,
        type: 'distance',
        id: `${telGuid}-distance`,
        message: G.getWindowLocale(
          'validation:recalculate-stop-distances',
          'Some stop distances need to be recalculated',
        ),
      }));
    }

    if (telError.containersError) {
      issues.push(createValidationIssue({
        telGuid,
        tripName,
        type: 'containers',
        message: G.getWindowLocale(
          'validation:invalid-containers',
          'Picked up and dropped containers are not balanced',
        ),
        id: `${telGuid}-containers`,
      }));
    }

    Object.entries(telError.eventsErrors || {}).forEach(([eventGuid, eventError]) => {
      const event = R.pathOr({}, [GC.FIELD_LOAD_STOPS, eventGuid], tel);

      const location = event[GC.SYSTEM_OBJECT_LOCATION]
        || R.pathOr({}, ['stop', GC.SYSTEM_OBJECT_LOCATION], event);

      const locationName = R.propOr('', GC.FIELD_LOCATION_NAME, location);

      const common = {
        telGuid,
        tripName,
        eventGuid,
        locationName,
        scope: 'stop',
        stopIndex: event[GC.FIELD_TEL_EVENT_INDEX],
      };

      if (eventError[GC.FIELD_DATE]) {
        issues.push(createValidationIssue({
          ...common,
          type: 'date',
          message: G.getWindowLocale(
            'validation:event-wrong-date',
            'The stop time is not in route order',
          ),
          id: `${telGuid}-${eventGuid}-date`,
        }));
      }

      const wrongItems = eventError[GC.FIELD_LOAD_ITEMS];

      if (G.isNotNilAndNotEmpty(wrongItems)) {
        const isPickup = G.isEventTypePickup(event[GC.FIELD_EVENT_TYPE]);

        const message = isPickup
          ? G.getWindowLocale(
            'validation:pickup-items-need-later-drop',
            'Picked up items must be dropped later in the trip',
          )
          : G.getWindowLocale(
            'validation:cant-drop-items-before-pickup',
            'Items cannot be dropped before they have been picked up',
          );

        issues.push(createValidationIssue({
          ...common,
          message,
          type: 'items',
          affectedCount: wrongItems.length,
          id: `${telGuid}-${eventGuid}-items`,
        }));
      }

      const itemContainers = R.prop('itemContainers', eventError);

      if (G.isNotNilAndNotEmpty(itemContainers)) {
        issues.push(createValidationIssue({
          ...common,
          type: 'itemContainers',
          message: G.getWindowLocale(
            'validation:items-should-be-in-container',
            'All items in the stop need to be added to a container',
          ),
          affectedCount: itemContainers.length,
          id: `${telGuid}-${eventGuid}-item-containers`,
        }));
      }
    });
  });

  return issues;
};

export const getRateGuidToCommitmentCountMap = (tels) => {
  const map = {};

  tels.forEach(({ rates }) => (
    R.or(rates, []).forEach(({ guid, commitmentCount }) => map[guid] = commitmentCount)
  ));

  return map;
};
// logic

export {
  addTelItems,
  sortTelEvents,
  getItemsToRemove,
  getContainerInfo,
  getTableMaxHeight,
  concatArrayItemsProp,
  filterPickedDroppedItems,
  getLoadEditModeStopsGeodata,
  getAvailableContainersForDrop,
  indexObjectArrayPropByPropName,
  getAvailableContainersForCloDrop,
  getAvailableExistedTerminalOptions,
  getExistingTerminalStopIdByLocation,
  createContainerFromFormValuesAndStop,
} from './shared/helpers';
